import { useQuery } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { useState } from "react";
import type { Page, Task } from "@estelle/domain";

import { useDataSource } from "@/adapters/data-source";
import { can, usePermissions } from "@/auth/permissions";
import { Fab } from "@/components/shell/Fab";
import { LoadFailed, NoMatches } from "@/components/shell/EmptyState";
import { TableSkeleton } from "@/components/shell/LoadingState";
import { RecordDrawer } from "@/components/shell/RecordDrawer";
import { DataTable } from "@/components/table/DataTable";
import { Pagination } from "@/components/table/Pagination";
import { useCursorHistory } from "@/components/table/useCursorHistory";
import { Button } from "@/components/ui/button";
import { matchesFilter } from "@/lib/filter-predicate";
import { reportingNow } from "@/lib/reportClock";
import {
  projectLabel,
  userLabel,
  useProjectDirectory,
  useUserDirectory,
} from "@/modules/tasks/directories";
import {
  isMineOrWatched,
  matchesTaskSearch,
  sortTasks,
  standingProjectId,
  TASK_FETCH_LIMIT,
} from "@/modules/tasks/list-helpers";
import type { SectionScreenProps } from "@/modules/types";
import { projectTask } from "@/modules/tasks/projections";
import { useOpenRecord } from "@/routes/useOpenRecord";
import { SubtaskBadge } from "@/modules/tasks/components/SubtaskBadge";
import { TaskDetail } from "@/modules/tasks/screens/TaskDetail";
import { TaskForm } from "@/modules/tasks/screens/TaskForm";

/**
 * `all` and `mine`, the two Tasks sections built from this file, are the
 * same list under a different standing narrowing — the `ordersList()` shape
 * from `modules/orders/screens/OrdersList.tsx`. What differs from that
 * precedent is *how* the narrowing is applied: Orders' "Unfulfilled" adds
 * one more condition to the `Filter` tree the repository already evaluates.
 * "My Tasks" cannot, because "assigned to or watched by me" needs
 * `watcherUserIds`, and the shared fixture adapter's own filtering
 * projection (in `adapters/fixture/index.ts`) never exposes it — the same
 * reason `overdue` is filtered here rather than server-side (see the
 * comment on it in `fields.ts`). So every Tasks list fetches the whole
 * dataset once and does the narrowing, the filtering, the sorting and the
 * paging itself, the way `modules/warehouses/screens/InTransit.tsx` does
 * for a repository that cannot support what its screen needs.
 */
export function taskList(scope?: "mine") {
  return function TaskList({
    fields,
    filter,
    search,
    sort,
    onToggleSort,
    onClearFilter,
  }: SectionScreenProps) {
    const data = useDataSource();
    const permissions = usePermissions();

    const [limit, setLimit] = useState(25);
    const [history, setHistory] = useCursorHistory();
    const cursor = history[history.length - 1];
    const start = cursor ? Number(cursor) : 0;
    const { recordId, open } = useOpenRecord();
    const [creating, setCreating] = useState(false);

    const usersQuery = useUserDirectory();
    const projectsQuery = useProjectDirectory();

    const currentUserQuery = useQuery({
      queryKey: ["tasks", "current-user"],
      queryFn: () => data.currentUser(),
      // Only "My Tasks" needs to know who is signed in; fetching it for
      // "All Tasks" would be a request nothing on that screen uses.
      enabled: scope === "mine",
    });

    const tasksQuery = useQuery({
      queryKey: ["tasks", "list", "fetch-all"],
      queryFn: () => data.tasks.list({ limit: TASK_FETCH_LIMIT }),
    });

    if (
      usersQuery.isPending ||
      projectsQuery.isPending ||
      tasksQuery.isPending ||
      (scope === "mine" && currentUserQuery.isPending)
    ) {
      return <TableSkeleton />;
    }

    if (usersQuery.isError) {
      return (
        <LoadFailed
          message={usersQuery.error.message}
          onRetry={() => {
            void usersQuery.refetch();
          }}
        />
      );
    }

    if (projectsQuery.isError) {
      return (
        <LoadFailed
          message={projectsQuery.error.message}
          onRetry={() => {
            void projectsQuery.refetch();
          }}
        />
      );
    }

    if (tasksQuery.isError) {
      return (
        <LoadFailed
          message={tasksQuery.error.message}
          onRetry={() => {
            void tasksQuery.refetch();
          }}
        />
      );
    }

    if (scope === "mine" && currentUserQuery.isError) {
      return (
        <LoadFailed
          message={currentUserQuery.error.message}
          onRetry={() => {
            void currentUserQuery.refetch();
          }}
        />
      );
    }

    const scoped =
      scope === "mine" && currentUserQuery.data
        ? tasksQuery.data.items.filter((task) =>
            isMineOrWatched(task, currentUserQuery.data.id),
          )
        : tasksQuery.data.items;

    const matching = scoped.filter(
      (task) =>
        matchesTaskSearch(task, search, usersQuery.data.items) &&
        matchesFilter(projectTask(task), filter, reportingNow()),
    );
    const sorted = sortTasks(matching, sort);
    const items = sorted.slice(start, start + limit);
    const page: Page<Task> = {
      items,
      nextCursor: start + limit < sorted.length ? String(start + limit) : null,
      total: sorted.length,
    };

    const users = usersQuery.data.items;
    const projects = projectsQuery.data.items;
    const standingProject = standingProjectId(filter);

    return (
      <div className="flex h-full min-h-0 flex-col">
        {/*
          Hidden without `tasks.task.create` — a courtesy, not the
          enforcement: `POST /api/tasks` calls `requirePermission()`
          regardless, and a rejection from it still renders through
          `TaskForm`'s own rejection text.
        */}
        {can(permissions, "tasks.task.create") ? (
          <>
            <div className="max-md:hidden flex shrink-0 justify-end border-b px-4 py-2">
              <Button
                type="button"
                size="sm"
                onClick={() => {
                  setCreating(true);
                }}
              >
                <Plus />
                New task
              </Button>
            </div>
            <Fab
              label="New task"
              icon={<Plus aria-hidden />}
              onClick={() => {
                setCreating(true);
              }}
            />
          </>
        ) : null}

        <TaskForm
          key={standingProject ?? "no-project"}
          open={creating}
          onOpenChange={setCreating}
          initialProjectId={standingProject}
          onCreated={(task) => {
            open(task.id);
          }}
        />

        {recordId !== null ? (
          <RecordDrawer
            title={
              tasksQuery.data.items.find((task) => task.id === recordId)
                ?.title ?? "Task"
            }
          >
            <TaskDetail taskId={recordId} />
          </RecordDrawer>
        ) : null}
        <DataTable
          fields={fields}
          rows={items}
          getRowId={(task) => task.id}
          project={projectTask}
          sort={sort}
          onToggleSort={onToggleSort}
          onRowClick={(task) => {
            open(task.id);
          }}
          renderOverride={(field, task) => {
            switch (field.name) {
              case "title":
                // A subtask says so before anyone clicks it (#666).
                return task.parentTaskId === null ? undefined : (
                  <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <span>{task.title}</span>
                    <SubtaskBadge task={task} all={tasksQuery.data.items} />
                  </span>
                );
              case "assigneeUserId":
                return task.assigneeUserId ? (
                  <span>{userLabel(users, task.assigneeUserId)}</span>
                ) : (
                  <span className="text-muted-foreground">Unassigned</span>
                );
              case "reporterUserId":
                return <span>{userLabel(users, task.reporterUserId)}</span>;
              case "projectId":
                return <span>{projectLabel(projects, task.projectId)}</span>;
              default:
                return undefined;
            }
          }}
          emptyState={
            <NoMatches
              onClear={onClearFilter}
              description={
                scope === "mine"
                  ? "No tasks assigned to or watched by you match the current filter."
                  : undefined
              }
            />
          }
          footer={
            <Pagination
              page={page}
              limit={limit}
              onLimitChange={(next) => {
                setLimit(next);
                setHistory([undefined]);
              }}
              history={history}
              onNavigate={setHistory}
            />
          }
        />
      </div>
    );
  };
}
